import { ApiResponse } from "types";
import { IMenu, IPotluck } from "interfaces";
import { Apis, Enums } from "utils";
import { Combines } from "helpers";

export interface IPotluckData {
  all(): Promise<ApiResponse<IPotluck[]>>;
  find(query: any, populated: boolean): Promise<ApiResponse<IPotluck>>;
  findAll(query: any): Promise<ApiResponse<IPotluck[]>>;
  insert(payload: IPotluck[]): Promise<ApiResponse<IPotluck[]>>;
  update(payload: IPotluck): Promise<ApiResponse<IPotluck>>;
  delete(id: string): Promise<ApiResponse<boolean>>;
  addParticipants(id: string, user: string[]): Promise<ApiResponse<IPotluck>>;
  removeParticipants(id: string, user: string[]): Promise<ApiResponse<IPotluck>>;
  voting(userId: string, potluckId: string, menu: IMenu[]): Promise<ApiResponse<IPotluck>>;
}

export class PotluckData implements IPotluckData {
  private collection = "potlucks";

  async all(): Promise<ApiResponse<IPotluck[]>> {
    try {
      const response = await Apis.current().get(`${this.collection}`);
      if (response.data.err) {
        return { status: Enums.EnumResponse.Error, payload: [] } as ApiResponse<IPotluck[]>;
      } else {
        return { status: Enums.EnumResponse.Success, payload: response.data } as ApiResponse<IPotluck[]>;
      }
    } catch (err) {
      return { status: Enums.EnumResponse.Error, payload: [] } as ApiResponse<IPotluck[]>;
    }
  }

  async find(query: any, populated: boolean): Promise<ApiResponse<IPotluck>> {
    try {
      const response = await Apis.current().post(`${this.collection}/find`, { ...query, populated: populated } as any);
      if (response.data.err) {
        return { status: Enums.EnumResponse.Error, payload: null } as ApiResponse<IPotluck>;
      } else {
        return { status: Enums.EnumResponse.Success, payload: response.data } as ApiResponse<IPotluck>;
      }
    } catch (err) {
      return { status: Enums.EnumResponse.Error, payload: null } as ApiResponse<IPotluck>;
    }
  }

  async findAll(query: any): Promise<ApiResponse<IPotluck[]>> {
    try {
      const response = await Apis.current().post(`${this.collection}/find-all`, query);
      if (response.data.err) {
        return { status: Enums.EnumResponse.Error, payload: null } as ApiResponse<IPotluck[]>;
      } else {
        return { status: Enums.EnumResponse.Success, payload: response.data } as ApiResponse<IPotluck[]>;
      }
    } catch (err) {
      return { status: Enums.EnumResponse.Error, payload: null } as ApiResponse<IPotluck[]>;
    }
  }

  async insert(payload: IPotluck[]): Promise<ApiResponse<IPotluck[]>> {
    try {
      const response = await Apis.current().post(`${this.collection}/bulk`, payload);
      if (response.data.err) {
        return { status: Enums.EnumResponse.Error, payload: [] } as ApiResponse<IPotluck[]>;
      } else {
        return { status: Enums.EnumResponse.Success, payload: response.data } as ApiResponse<IPotluck[]>;
      }
    } catch (err) {
      return { status: Enums.EnumResponse.Error, payload: [] } as ApiResponse<IPotluck[]>;
    }
  }

  async update(payload: IPotluck): Promise<ApiResponse<IPotluck>> {
    try {
      const response = await Apis.current().put(`${this.collection}`, payload);
      if (response.data.err) {
        return { status: Enums.EnumResponse.Error, payload: null } as ApiResponse<IPotluck>;
      } else {
        return { status: Enums.EnumResponse.Success, payload: response.data } as ApiResponse<IPotluck>;
      }
    } catch (err) {
      return { status: Enums.EnumResponse.Error, payload: null } as ApiResponse<IPotluck>;
    }
  }

  async delete(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await Apis.current().delete(`${this.collection}/${id}`);
      if (response.data.err) {
        return { status: Enums.EnumResponse.Error, payload: false } as ApiResponse<boolean>;
      } else {
        return { status: Enums.EnumResponse.Success, payload: true } as ApiResponse<boolean>;
      }
    } catch (err) {
      return { status: Enums.EnumResponse.Error, payload: false } as ApiResponse<boolean>;
    }
  }

  async addParticipants(id: string, users: string[]): Promise<ApiResponse<IPotluck>> {
    try {
      const response = await Apis.current().patch(`${this.collection}/participants/add`, { id, users });
      if (response.data.err) {
        return { status: Enums.EnumResponse.Error, payload: null } as ApiResponse<IPotluck>;
      } else {
        return { status: Enums.EnumResponse.Success, payload: response.data } as ApiResponse<IPotluck>;
      }
    } catch (err) {
      return { status: Enums.EnumResponse.Error, payload: null } as ApiResponse<IPotluck>;
    }
  }

  async removeParticipants(id: string, users: string[]): Promise<ApiResponse<IPotluck>> {
    try {
      const response = await Apis.current().patch(`${this.collection}/participants/remove`, { id, users });
      if (response.data.err) {
        return { status: Enums.EnumResponse.Error, payload: null } as ApiResponse<IPotluck>;
      } else {
        return { status: Enums.EnumResponse.Success, payload: response.data } as ApiResponse<IPotluck>;
      }
    } catch (err) {
      return { status: Enums.EnumResponse.Error, payload: null } as ApiResponse<IPotluck>;
    }
  }

  async voting(userId: string, potluckId: string, menu: IMenu[]): Promise<ApiResponse<IPotluck>> {
    try {
      const response = await Apis.current().patch(Combines.interpolateURL(`${this.collection}/voting/:id`, { id: userId }), { id: potluckId, menu });
      if (response.data.err) {
        return { status: Enums.EnumResponse.Error, payload: null } as ApiResponse<IPotluck>;
      } else {
        return { status: Enums.EnumResponse.Success, payload: response.data } as ApiResponse<IPotluck>;
      }
    } catch (err) {
      return { status: Enums.EnumResponse.Error, payload: null } as ApiResponse<IPotluck>;
    }
  }
}